import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Archive, ArrowLeft, Download, FileText, Pencil, Plus, RefreshCcw, Search, Trash2, Upload, Warehouse } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api from "@/lib/api";
import { useAuth, isManagerOrAdmin } from "@/lib/auth.jsx";
import { formatDate, money } from "@/lib/format";
import { getApiErrorMessage } from "@/lib/apiError";

const STATUS_OPTIONS = ["On Site - Display", "On Site - Available", "Ordered", "In Production", "In Transit", "Delivered", "Sold / Reserved", "Pending Setup", "Unavailable", "Archived"];
const HOME_TYPES = ["Single-wide", "Double-wide", "Triple-wide", "Modular"];
const FINANCING_LENDERS = ["21st Mortgage", "CountryPlace", "Triad", "Cash", "25% Down Cash", "Manufacturer", "Legacy", "TBD"];
const TAX_PERIODS = ["2025 Q1", "2025 Q2", "2025 Q3", "2025 Q4", "2026 Q1", "2026 Q2", "2026 Q3", "2026 Q4", "2027 Q1", "2027 Q2", "2027 Q3", "2027 Q4", "TBD"];
const TAX_EXEMPT_OPTIONS = ["YES", "NO", "TBD"];
const TAX_FILING_STATUSES = ["Filed", "Not Filed", "TAX EXEMPT", "TBD"];
const COUNTY_SURTAX_OPTIONS = ["Hernando 0.5%", "Pasco 1%", "TAX EXEMPT", "TBD"];
const LINK_DOCUMENT_CATEGORIES = ["Final Invoice", "Transport Invoice", "Floorplan / Elevation", "MCO / MSO", "Data Plate", "Other Inventory Documents"];
const UPLOAD_DOCUMENT_CATEGORIES = ["Final Invoice", "Transport Invoice", "Floorplan / Elevation", "MCO / MSO", "Data Plate", "Inventory Photos", "Other Inventory Documents"];
const ON_SITE_STATUSES = new Set(["On Site - Display", "On Site - Available"]);
const DELIVERY_TRACKED_STATUSES = new Set(["Ordered", "In Production", "In Transit"]);
const SERIAL_REQUIRED_STATUSES = new Set(["On Site - Display", "On Site - Available", "Delivered"]);
const PROTECTED_DOCUMENTS = new Set(["Final Invoice", "Transport Invoice", "MCO / MSO", "Data Plate"]);
const GRIDFS_ONLY_DOCUMENTS = new Set(["Inventory Photos"]);

const EMPTY_DOCUMENT = { category: "Other Inventory Documents", name: "", url: "", storage_key: "", notes: "" };
const UPLOAD_DEFAULTS = { category: "Final Invoice", notes: "", protected: true, file: null };
const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;
const ALLOWED_UPLOAD_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
const BLOCKED_UPLOAD_EXTENSIONS = new Set(["exe", "bat", "cmd", "js", "sh", "com", "scr", "msi", "dll"]);
const EMPTY_ITEM = {
    catalog_home_id: "",
    name: "",
    manufacturer: "",
    series: "",
    status: "On Site - Available",
    serial_number: "",
    hud_labels: "",
    vin: "",
    model_year: "",
    dimensions: "",
    beds: "",
    baths: "",
    sqft: "",
    home_type: "",
    current_location: "",
    display_location: "",
    lot_position: "",
    ordered_date: "",
    delivered_date: "",
    sold_date: "",
    expected_delivery_date: "",
    estimated_offline_date: "",
    arrival_date: "",
    buyer_client_id: "",
    buyer_client_name: "",
    buyer_phone: "",
    buyer_email: "",
    lead_id: "",
    customer_id: "",
    customer_name: "",
    quote_id: "",
    project_id: "",
    financing_type: "",
    financing_lender: "TBD",
    financing_status: "",
    lender_account_reference: "",
    invoice_number: "",
    final_home_only_sales_price: "",
    invoice_without_freight: "",
    freight_financed: "",
    freight_paid: "",
    final_invoice_total: "",
    customer_price: "",
    internal_cost: "",
    closing_date: "",
    tax_period: "TBD",
    tax_exempt_status: "TBD",
    sales_tax: "",
    discretionary_sales_surtax: "",
    tax_due: "",
    sales_tax_filing_status: "TBD",
    county_tax_filing_status: "TBD",
    destination_county_surtax_bracket: "TBD",
    documents: [],
    image_url: "",
    floorplan_image_url: "",
    notes: "",
    active: true,
    archived: false,
};

function statusClass(status) {
    if (status === "On Site - Available") return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (status === "On Site - Display") return "bg-teal-50 text-teal-700 border-teal-200";
    if (status === "Ordered" || status === "In Production") return "bg-blue-50 text-blue-700 border-blue-200";
    if (status === "In Transit" || status === "Pending Setup") return "bg-amber-50 text-amber-800 border-amber-200";
    if (status === "Sold / Reserved") return "bg-violet-50 text-violet-700 border-violet-200";
    if (status === "Delivered") return "bg-cyan-50 text-cyan-700 border-cyan-200";
    if (status === "Unavailable") return "bg-rose-50 text-rose-700 border-rose-200";
    if (status === "Archived") return "bg-slate-100 text-slate-600 border-slate-200";
    return "bg-slate-50 text-slate-700 border-slate-200";
}

function StatusPill({ status }) {
    return <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${statusClass(status)}`}>{status || "Missing status"}</span>;
}

function moneyOrDash(value) {
    if (value === null || value === undefined || value === "") return "-";
    return money(value, { decimals: 0 });
}

function specLine(item) {
    const bits = [];
    if (item.dimensions) bits.push(item.dimensions);
    if (item.beds || item.baths) bits.push(`${item.beds || "-"} bd / ${item.baths || "-"} ba`);
    if (item.sqft) bits.push(`${item.sqft} sqft`);
    return bits.join(" | ") || "Specs not set";
}

function locationLine(item) {
    return [item.current_location, item.display_location, item.lot_position].filter(Boolean).join(" / ") || "-";
}

function serialLine(item) {
    return [item.serial_number ? `Serial ${item.serial_number}` : null, item.hud_labels ? `HUD ${item.hud_labels}` : null, item.vin ? `VIN ${item.vin}` : null].filter(Boolean).join(" | ");
}

function buyerName(item) {
    return item.buyer_client_name || item.customer_name || "-";
}

function importantDate(item) {
    if (item.sold_date) return `Sold ${formatDate(item.sold_date)}`;
    if (item.delivered_date) return `Delivered ${formatDate(item.delivered_date)}`;
    if (item.expected_delivery_date && ["Ordered", "In Production", "In Transit"].includes(item.status)) return `Expected ${formatDate(item.expected_delivery_date)}`;
    if (item.arrival_date) return `Arrived ${formatDate(item.arrival_date)}`;
    if (item.ordered_date) return `Ordered ${formatDate(item.ordered_date)}`;
    return "-";
}

function isOverdue(value) {
    if (!value) return false;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    parsed.setHours(0, 0, 0, 0);
    return parsed < today;
}

function hasDocument(item, category) {
    return (item.documents || []).some((doc) => !doc.archived && doc.category === category);
}

function formatBytes(bytes) {
    if (!bytes && bytes !== 0) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function documentDisplayName(doc) {
    return doc.original_filename || doc.name || doc.file_name || doc.url || doc.storage_key || "Inventory document";
}

function uploadProtectedDefault(category) {
    return PROTECTED_DOCUMENTS.has(category);
}

function validateSelectedUpload(file) {
    if (!file) return "Choose a document to upload";
    const extension = (file.name.split(".").pop() || "").toLowerCase();
    if (BLOCKED_UPLOAD_EXTENSIONS.has(extension)) return "This file type is not allowed for inventory documents";
    if (file.size > MAX_DOCUMENT_BYTES) return "Document is too large. Upload files up to 25MB.";
    if (file.type && !ALLOWED_UPLOAD_TYPES.includes(file.type)) return "Upload a PDF, JPG, PNG, or WEBP document";
    return null;
}

function attentionReasons(item) {
    if (Array.isArray(item.needs_attention_reasons) && item.needs_attention_reasons.length) return item.needs_attention_reasons;
    const reasons = [];
    if (!item.status) reasons.push("Missing status");
    if (!item.name) reasons.push("Missing model/name");
    if (DELIVERY_TRACKED_STATUSES.has(item.status) && !item.expected_delivery_date) reasons.push("Missing expected delivery date");
    if (DELIVERY_TRACKED_STATUSES.has(item.status) && isOverdue(item.expected_delivery_date)) reasons.push("Expected delivery is overdue");
    if (ON_SITE_STATUSES.has(item.status) && !item.current_location && !item.display_location && !item.lot_position) reasons.push("Missing on-site location or lot position");
    if (item.status === "Delivered" && !item.delivered_date) reasons.push("Missing delivered date");
    if (item.status === "Sold / Reserved" && !item.buyer_client_name && !item.buyer_client_id && !item.customer_id && !item.project_id) reasons.push("Missing buyer/client link");
    if (SERIAL_REQUIRED_STATUSES.has(item.status) && !item.serial_number && !item.vin) reasons.push("Missing serial number or VIN");
    if (["Sold / Reserved", "Delivered"].includes(item.status) && !hasDocument(item, "Final Invoice")) reasons.push("Missing final invoice");
    if (["Sold / Reserved", "Delivered"].includes(item.status) && !hasDocument(item, "MCO / MSO")) reasons.push("Missing MCO/MSO");
    if (["On Site - Display", "On Site - Available", "Delivered"].includes(item.status) && !hasDocument(item, "Data Plate")) reasons.push("Missing data plate");
    if (item.closing_date && item.sales_tax_filing_status === "Not Filed") reasons.push("Sales tax not filed after closing");
    if (item.closing_date && (!item.destination_county_surtax_bracket || item.destination_county_surtax_bracket === "TBD")) reasons.push("Destination county/surtax bracket TBD after closing");
    return reasons;
}

function linkedCustomer(item) {
    if (item.customer_id) return <Link className="text-[#1E6FA8] hover:underline" to={`/customers/${item.customer_id}`}>{buyerName(item)}</Link>;
    return buyerName(item);
}

function linkedProject(item) {
    if (item.project_id) return <Link className="text-[#1E6FA8] hover:underline" to={`/projects/${item.project_id}`}>Project</Link>;
    return null;
}

function documentSummary(item) {
    const indicators = [
        ["Final Invoice", "Final Invoice"],
        ["Transport Invoice", "Transport"],
        ["MCO / MSO", "MCO/MSO"],
        ["Data Plate", "Data Plate"],
        ["Floorplan / Elevation", "Floorplan"],
    ];
    return indicators.map(([category, label]) => `${hasDocument(item, category) ? "✓" : "Missing"} ${label}`).join(" | ");
}

function FormSection({ title, children }) {
    return <div className="rounded-lg border border-slate-200 p-3"><div className="mb-3 text-sm font-semibold text-slate-900">{title}</div><div className="grid grid-cols-1 gap-3 md:grid-cols-2">{children}</div></div>;
}

function Field({ label, children, className = "" }) {
    return <div className={className}><Label>{label}</Label>{children}</div>;
}

function TextInput({ value, onChange, type = "text", placeholder }) {
    return <Input type={type} value={value || ""} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />;
}

function SelectInput({ value, onChange, options, emptyLabel = "Select" }) {
    return (
        <select value={value || ""} onChange={(event) => onChange(event.target.value)} className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
            <option value="">{emptyLabel}</option>
            {options.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
    );
}

function InventoryDocumentsSection({ inventoryId, documents, setDocuments, onDocumentsChanged }) {
    const [upload, setUpload] = useState(UPLOAD_DEFAULTS);
    const [uploading, setUploading] = useState(false);

    const setUploadField = (key, next) => setUpload((current) => {
        const updated = { ...current, [key]: next };
        if (key === "category" && next !== "Other Inventory Documents") updated.protected = uploadProtectedDefault(next);
        return updated;
    });

    const refreshDocuments = async () => {
        if (!inventoryId) return;
        const response = await api.get(`/home-inventory/${inventoryId}/documents`);
        setDocuments(response.data || []);
        onDocumentsChanged?.(response.data || []);
    };

    const uploadDocument = async () => {
        const friendlyError = validateSelectedUpload(upload.file);
        if (friendlyError) {
            toast.error(friendlyError);
            return;
        }
        if (!inventoryId) {
            toast.error("Save the inventory item before uploading files");
            return;
        }
        const formData = new FormData();
        formData.append("file", upload.file);
        formData.append("category", upload.category);
        formData.append("notes", upload.notes || "");
        formData.append("protected", upload.protected ? "true" : "false");
        setUploading(true);
        try {
            await api.post(`/home-inventory/${inventoryId}/documents/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Document uploaded");
            setUpload({ ...UPLOAD_DEFAULTS, file: null });
            const input = document.getElementById("inventory-document-upload");
            if (input) input.value = "";
            await refreshDocuments();
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Document upload failed"));
        } finally {
            setUploading(false);
        }
    };

    const downloadDocument = async (doc) => {
        if (doc.url && !doc.storage_provider) {
            window.open(doc.url, "_blank", "noopener,noreferrer");
            return;
        }
        const documentId = doc.document_id || doc.file_id;
        if (!inventoryId || !documentId) {
            toast.error("Document download is not available");
            return;
        }
        try {
            const response = await api.get(`/home-inventory/${inventoryId}/documents/${documentId}/download`, { responseType: "blob" });
            const href = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = href;
            link.download = documentDisplayName(doc);
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(href);
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Download failed"));
        }
    };

    const archiveDocument = async (doc) => {
        const documentId = doc.document_id || doc.file_id;
        if (!documentId) return;
        if (!window.confirm(`Archive ${documentDisplayName(doc)}?`)) return;
        try {
            await api.delete(`/home-inventory/${inventoryId}/documents/${documentId}`);
            toast.success("Document archived");
            await refreshDocuments();
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Archive failed"));
        }
    };

    return <div className="md:col-span-2 space-y-4">
        <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-blue-950"><Upload className="h-4 w-4" /> Upload Document</div>
            {!inventoryId ? <p className="mb-3 text-sm text-blue-800">Save this inventory item first, then reopen it to upload PDFs, invoices, data plates, and floorplans.</p> : null}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Field label="Category"><SelectInput value={upload.category} onChange={(next) => setUploadField("category", next)} options={UPLOAD_DOCUMENT_CATEGORIES} emptyLabel="Choose category" /></Field>
                <Field label="Choose file"><Input id="inventory-document-upload" type="file" accept="application/pdf,image/jpeg,image/png,image/webp" disabled={!inventoryId || uploading} onChange={(event) => setUploadField("file", event.target.files?.[0] || null)} /></Field>
                <Field label="Notes" className="md:col-span-2"><Textarea rows={2} value={upload.notes || ""} onChange={(event) => setUploadField("notes", event.target.value)} placeholder="Optional document notes" /></Field>
                <label className="flex items-center gap-2 text-sm text-slate-700 md:col-span-2">
                    <input type="checkbox" checked={upload.protected || PROTECTED_DOCUMENTS.has(upload.category)} disabled={PROTECTED_DOCUMENTS.has(upload.category) || upload.category !== "Other Inventory Documents"} onChange={(event) => setUploadField("protected", event.target.checked)} />
                    Protected / internal document
                    {PROTECTED_DOCUMENTS.has(upload.category) ? <span className="text-xs text-slate-500">Required for this category</span> : null}
                </label>
            </div>
            <div className="mt-3 flex items-center justify-between gap-2 text-xs text-slate-600">
                <span>PDF, JPG, PNG, and WEBP up to 25MB.</span>
                <Button type="button" variant="outline" onClick={uploadDocument} disabled={!inventoryId || uploading}>{uploading ? "Uploading..." : "Upload Document"}</Button>
            </div>
        </div>

        <div className="space-y-2">
            <div className="text-sm font-semibold text-slate-900">Uploaded & linked documents</div>
            {(documents || []).length === 0 ? <p className="text-sm text-slate-500">No inventory documents attached.</p> : null}
            {(documents || []).map((doc, index) => (
                <div key={doc.document_id || doc.file_id || index} className="rounded-md border border-slate-200 p-3">
                    <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                        <div>
                            <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-900"><span>{doc.category || "Other Inventory Documents"}</span>{doc.protected ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">Protected/internal</span> : <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">Customer-facing</span>}</div>
                            <div className="mt-1 text-sm text-slate-700">{documentDisplayName(doc)}</div>
                            <div className="mt-1 text-xs text-slate-500">{[doc.content_type, formatBytes(doc.size_bytes), doc.uploaded_at ? `Uploaded ${formatDate(doc.uploaded_at)}` : null, doc.uploaded_by_name || doc.uploaded_by ? `by ${doc.uploaded_by_name || doc.uploaded_by}` : null].filter(Boolean).join(" | ")}</div>
                            {doc.notes ? <div className="mt-2 text-xs text-slate-600">{doc.notes}</div> : null}
                            {doc.url && !doc.storage_provider ? <div className="mt-1 text-xs text-slate-500">Existing linked document</div> : null}
                        </div>
                        <div className="flex gap-2">
                            {(doc.storage_provider || doc.url) ? <Button type="button" size="sm" variant="outline" onClick={() => downloadDocument(doc)}><Download className="mr-1 h-3.5 w-3.5" /> Download/View</Button> : null}
                            <Button type="button" size="sm" variant="outline" onClick={() => archiveDocument(doc)}><Trash2 className="mr-1 h-3.5 w-3.5" /> Archive</Button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </div>;
}

function InventoryForm({ value, setValue, homes, canSeeCost, inventoryId, onDocumentsChanged }) {
    const set = (key, next) => setValue((current) => ({ ...current, [key]: next }));
    const warnings = attentionReasons(value);

    const applyCatalogHome = (homeId) => {
        const home = homes.find((h) => h.id === homeId);
        if (!home) {
            set("catalog_home_id", "");
            return;
        }
        setValue((current) => ({
            ...current,
            catalog_home_id: home.id,
            name: home.model_name || current.name || "",
            manufacturer: home.manufacturer || current.manufacturer || "",
            series: home.series || current.series || "",
            dimensions: home.dimensions || current.dimensions || "",
            beds: home.beds ?? current.beds ?? "",
            baths: home.baths ?? current.baths ?? "",
            sqft: home.sqft ?? current.sqft ?? "",
            home_type: home.home_type || current.home_type || "",
            customer_price: home.ehs_price || home.msrp || current.customer_price || "",
            image_url: home.image_url || home.primary_image_url || home.photo_url || home.exterior_image_url || home.image || current.image_url || "",
            floorplan_image_url: home.floorplan_image_url || home.floorplan_url || home.floor_plan_url || home.plan_image_url || current.floorplan_image_url || "",
        }));
    };

    const addDocument = () => setValue((current) => ({ ...current, documents: [...(current.documents || []), { ...EMPTY_DOCUMENT }] }));
    const updateDocument = (index, key, next) => setValue((current) => ({
        ...current,
        documents: (current.documents || []).map((doc, docIndex) => docIndex === index ? { ...doc, [key]: next } : doc),
    }));
    const removeDocument = (index) => setValue((current) => ({ ...current, documents: (current.documents || []).filter((_, docIndex) => docIndex !== index) }));

    return (
        <div className="max-h-[72vh] space-y-3 overflow-y-auto pr-1">
            {warnings.length ? <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"><div className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4" /> Needs attention</div><div className="mt-1">{warnings.join("; ")}</div></div> : null}

            <FormSection title="Basic Home Info">
                <Field label="Catalog home link" className="md:col-span-2">
                    <select value={value.catalog_home_id || ""} onChange={(event) => applyCatalogHome(event.target.value)} className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                        <option value="">No linked catalog home</option>
                        {homes.map((home) => <option key={home.id} value={home.id}>{[home.manufacturer, home.series, home.model_name].filter(Boolean).join(" - ")}</option>)}
                    </select>
                </Field>
                <Field label="Model / home name *"><TextInput value={value.name} onChange={(next) => set("name", next)} /></Field>
                <Field label="Manufacturer"><TextInput value={value.manufacturer} onChange={(next) => set("manufacturer", next)} /></Field>
                <Field label="Series"><TextInput value={value.series} onChange={(next) => set("series", next)} /></Field>
                <Field label="Model year"><TextInput value={value.model_year} onChange={(next) => set("model_year", next)} /></Field>
                <Field label="Home type"><SelectInput value={value.home_type} onChange={(next) => set("home_type", next)} options={HOME_TYPES} /></Field>
                <Field label="Dimensions / size"><TextInput value={value.dimensions} onChange={(next) => set("dimensions", next)} placeholder="28x52" /></Field>
                <Field label="Beds"><TextInput type="number" value={value.beds} onChange={(next) => set("beds", next)} /></Field>
                <Field label="Baths"><TextInput type="number" value={value.baths} onChange={(next) => set("baths", next)} /></Field>
                <Field label="Square footage"><TextInput type="number" value={value.sqft} onChange={(next) => set("sqft", next)} /></Field>
                <Field label="Serial number"><TextInput value={value.serial_number} onChange={(next) => set("serial_number", next)} /></Field>
                <Field label="HUD label(s)"><TextInput value={value.hud_labels} onChange={(next) => set("hud_labels", next)} /></Field>
                <Field label="VIN"><TextInput value={value.vin} onChange={(next) => set("vin", next)} /></Field>
            </FormSection>

            <FormSection title="Status / Location / Dates">
                <Field label="Status *"><SelectInput value={value.status || "On Site - Available"} onChange={(next) => set("status", next)} options={STATUS_OPTIONS} emptyLabel="Select status" /></Field>
                <Field label="Current location"><TextInput value={value.current_location} onChange={(next) => set("current_location", next)} /></Field>
                <Field label="Display location"><TextInput value={value.display_location} onChange={(next) => set("display_location", next)} /></Field>
                <Field label="Lot position"><TextInput value={value.lot_position} onChange={(next) => set("lot_position", next)} /></Field>
                <Field label="Ordered date"><TextInput type="date" value={value.ordered_date} onChange={(next) => set("ordered_date", next)} /></Field>
                <Field label="Expected delivery date"><TextInput type="date" value={value.expected_delivery_date} onChange={(next) => set("expected_delivery_date", next)} /></Field>
                <Field label="Arrival date"><TextInput type="date" value={value.arrival_date} onChange={(next) => set("arrival_date", next)} /></Field>
                <Field label="Delivered date"><TextInput type="date" value={value.delivered_date} onChange={(next) => set("delivered_date", next)} /></Field>
                <Field label="Sold date"><TextInput type="date" value={value.sold_date} onChange={(next) => set("sold_date", next)} /></Field>
                <Field label="Estimated offline date"><TextInput type="date" value={value.estimated_offline_date} onChange={(next) => set("estimated_offline_date", next)} /></Field>
            </FormSection>

            <FormSection title="Buyer / Client">
                <Field label="Buyer/client name"><TextInput value={value.buyer_client_name} onChange={(next) => set("buyer_client_name", next)} /></Field>
                <Field label="Buyer/client ID"><TextInput value={value.buyer_client_id} onChange={(next) => set("buyer_client_id", next)} /></Field>
                <Field label="Buyer phone"><TextInput value={value.buyer_phone} onChange={(next) => set("buyer_phone", next)} /></Field>
                <Field label="Buyer email"><TextInput value={value.buyer_email} onChange={(next) => set("buyer_email", next)} /></Field>
                <Field label="Linked lead ID"><TextInput value={value.lead_id} onChange={(next) => set("lead_id", next)} /></Field>
                <Field label="Linked customer ID"><TextInput value={value.customer_id} onChange={(next) => set("customer_id", next)} /></Field>
                <Field label="Customer name"><TextInput value={value.customer_name} onChange={(next) => set("customer_name", next)} /></Field>
                <Field label="Linked project ID"><TextInput value={value.project_id} onChange={(next) => set("project_id", next)} /></Field>
                <Field label="Linked quote ID"><TextInput value={value.quote_id} onChange={(next) => set("quote_id", next)} /></Field>
            </FormSection>

            <FormSection title="Financing">
                <Field label="Financing lender"><SelectInput value={value.financing_lender} onChange={(next) => set("financing_lender", next)} options={FINANCING_LENDERS} /></Field>
                <Field label="Financing type"><TextInput value={value.financing_type} onChange={(next) => set("financing_type", next)} /></Field>
                <Field label="Financing status"><TextInput value={value.financing_status} onChange={(next) => set("financing_status", next)} /></Field>
                <Field label="Lender account reference"><TextInput value={value.lender_account_reference} onChange={(next) => set("lender_account_reference", next)} /></Field>
            </FormSection>

            <FormSection title="Invoice / Pricing">
                <Field label="Customer-facing price"><TextInput type="number" value={value.customer_price} onChange={(next) => set("customer_price", next)} /></Field>
                <Field label="Final home-only sales price"><TextInput type="number" value={value.final_home_only_sales_price} onChange={(next) => set("final_home_only_sales_price", next)} /></Field>
                <Field label="Invoice number"><TextInput value={value.invoice_number} onChange={(next) => set("invoice_number", next)} /></Field>
                {canSeeCost ? <><Field label="Internal cost"><TextInput type="number" value={value.internal_cost} onChange={(next) => set("internal_cost", next)} /></Field><Field label="Invoice without freight"><TextInput type="number" value={value.invoice_without_freight} onChange={(next) => set("invoice_without_freight", next)} /></Field><Field label="Freight financed"><TextInput type="number" value={value.freight_financed} onChange={(next) => set("freight_financed", next)} /></Field><Field label="Freight paid"><TextInput type="number" value={value.freight_paid} onChange={(next) => set("freight_paid", next)} /></Field><Field label="Final invoice total"><TextInput type="number" value={value.final_invoice_total} onChange={(next) => set("final_invoice_total", next)} /></Field></> : null}
            </FormSection>

            <FormSection title="Tax Tracking">
                <Field label="Closing date"><TextInput type="date" value={value.closing_date} onChange={(next) => set("closing_date", next)} /></Field>
                <Field label="Tax period"><SelectInput value={value.tax_period} onChange={(next) => set("tax_period", next)} options={TAX_PERIODS} /></Field>
                <Field label="Tax exempt status"><SelectInput value={value.tax_exempt_status} onChange={(next) => set("tax_exempt_status", next)} options={TAX_EXEMPT_OPTIONS} /></Field>
                <Field label="Sales tax filing status"><SelectInput value={value.sales_tax_filing_status} onChange={(next) => set("sales_tax_filing_status", next)} options={TAX_FILING_STATUSES} /></Field>
                <Field label="County tax filing status"><SelectInput value={value.county_tax_filing_status} onChange={(next) => set("county_tax_filing_status", next)} options={TAX_FILING_STATUSES} /></Field>
                <Field label="Destination county / surtax bracket"><SelectInput value={value.destination_county_surtax_bracket} onChange={(next) => set("destination_county_surtax_bracket", next)} options={COUNTY_SURTAX_OPTIONS} /></Field>
                {canSeeCost ? <><Field label="Sales tax"><TextInput type="number" value={value.sales_tax} onChange={(next) => set("sales_tax", next)} /></Field><Field label="Discretionary sales surtax"><TextInput type="number" value={value.discretionary_sales_surtax} onChange={(next) => set("discretionary_sales_surtax", next)} /></Field><Field label="Tax due"><TextInput type="number" value={value.tax_due} onChange={(next) => set("tax_due", next)} /></Field></> : null}
            </FormSection>

            {canSeeCost ? <FormSection title="Documents">
                <InventoryDocumentsSection inventoryId={inventoryId} documents={value.documents || []} setDocuments={(docs) => set("documents", docs)} onDocumentsChanged={onDocumentsChanged} />
                <div className="md:col-span-2 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="text-sm font-semibold text-slate-900">Attach Existing Document Link</div>
                    <p className="text-xs text-slate-500">Use this only for legacy Monday.com/external links. Inventory Photos must be uploaded through Upload Document above.</p>
                    {(value.documents || []).map((doc, originalIndex) => ({ doc, originalIndex })).filter(({ doc }) => !doc.storage_provider).map(({ doc, originalIndex }) => (
                        <div key={doc.file_id || originalIndex} className="rounded-md border border-slate-200 bg-white p-3">
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                <Field label="Category"><SelectInput value={doc.category} onChange={(next) => updateDocument(originalIndex, "category", next)} options={LINK_DOCUMENT_CATEGORIES} /></Field>
                                <Field label="File name"><TextInput value={doc.name} onChange={(next) => updateDocument(originalIndex, "name", next)} /></Field>
                                <Field label="File URL"><TextInput value={doc.url} onChange={(next) => updateDocument(originalIndex, "url", next)} /></Field>
                                <Field label="Storage key"><TextInput value={doc.storage_key} onChange={(next) => updateDocument(originalIndex, "storage_key", next)} /></Field>
                                <Field label="Notes" className="md:col-span-2"><Textarea rows={2} value={doc.notes || ""} onChange={(event) => updateDocument(originalIndex, "notes", event.target.value)} /></Field>
                            </div>
                            <div className="mt-2 flex items-center justify-between gap-2 text-xs text-slate-500"><span>{GRIDFS_ONLY_DOCUMENTS.has(doc.category) ? "Upload-only document" : PROTECTED_DOCUMENTS.has(doc.category) ? "Protected document" : "Customer-facing document"}</span><Button type="button" size="sm" variant="outline" onClick={() => removeDocument(originalIndex)}><Trash2 className="mr-1 h-3.5 w-3.5" /> Remove link</Button></div>
                        </div>
                    ))}
                    <Button type="button" variant="outline" onClick={addDocument}><Plus className="mr-2 h-4 w-4" /> Attach Existing Document Link</Button>
                </div>
            </FormSection> : null}

            <FormSection title="Media">
                <Field label="Home photo URL"><TextInput value={value.image_url} onChange={(next) => set("image_url", next)} /></Field>
                <Field label="Floorplan image URL"><TextInput value={value.floorplan_image_url} onChange={(next) => set("floorplan_image_url", next)} /></Field>
            </FormSection>

            <FormSection title="Notes">
                <Field label="Notes" className="md:col-span-2"><Textarea rows={3} value={value.notes || ""} onChange={(event) => set("notes", event.target.value)} /></Field>
            </FormSection>
        </div>
    );
}

export default function HomeInventory() {
    const { user } = useAuth();
    const canManage = isManagerOrAdmin(user);
    const [items, setItems] = useState([]);
    const [homes, setHomes] = useState([]);
    const [summary, setSummary] = useState({});
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState("");
    const [status, setStatus] = useState("__all__");
    const [manufacturer, setManufacturer] = useState("__all__");
    const [homeType, setHomeType] = useState("__all__");
    const [financingLender, setFinancingLender] = useState("__all__");
    const [taxPeriod, setTaxPeriod] = useState("__all__");
    const [taxStatus, setTaxStatus] = useState("__all__");
    const [countyTaxStatus, setCountyTaxStatus] = useState("__all__");
    const [quickFilter, setQuickFilter] = useState("all");
    const [archiveView, setArchiveView] = useState("active");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_ITEM);

    const load = async () => {
        setLoading(true);
        try {
            const params = {
                q: query || undefined,
                status: status !== "__all__" ? status : undefined,
                manufacturer: manufacturer !== "__all__" ? manufacturer : undefined,
                home_type: homeType !== "__all__" ? homeType : undefined,
                financing_lender: financingLender !== "__all__" ? financingLender : undefined,
                tax_period: taxPeriod !== "__all__" ? taxPeriod : undefined,
                sales_tax_filing_status: taxStatus !== "__all__" ? taxStatus : undefined,
                county_tax_filing_status: countyTaxStatus !== "__all__" ? countyTaxStatus : undefined,
                on_site_only: quickFilter === "on_site",
                available_only: quickFilter === "available",
                sold_reserved: quickFilter === "sold_reserved",
                missing_invoice: quickFilter === "missing_invoice",
                missing_mco: quickFilter === "missing_mco",
                archive_view: canManage ? archiveView : "active",
                include_archived: canManage && archiveView !== "active",
            };
            const [inventoryResult, summaryResult, homesResult] = await Promise.allSettled([
                api.get("/home-inventory", { params }),
                api.get("/home-inventory/summary"),
                canManage ? api.get("/homes", { params: { include_inactive: true, limit: 1000 } }) : Promise.resolve({ data: [] }),
            ]);
            setItems(inventoryResult.status === "fulfilled" ? inventoryResult.value.data || [] : []);
            setSummary(summaryResult.status === "fulfilled" ? summaryResult.value.data || {} : {});
            setHomes(homesResult.status === "fulfilled" ? homesResult.value.data || [] : []);
        } catch (err) {
            toast.error("Unable to load home inventory");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status, manufacturer, homeType, financingLender, taxPeriod, taxStatus, countyTaxStatus, quickFilter, archiveView]);

    const manufacturers = useMemo(() => Array.from(new Set(items.map((item) => item.manufacturer).filter(Boolean))).sort(), [items]);
    const types = useMemo(() => Array.from(new Set([...HOME_TYPES, ...items.map((item) => item.home_type).filter(Boolean)])).sort(), [items]);

    const startCreate = () => {
        setEditing(null);
        setForm({ ...EMPTY_ITEM, documents: [] });
        setDialogOpen(true);
    };

    const startEdit = (item) => {
        setEditing(item);
        setForm({ ...EMPTY_ITEM, ...item, catalog_home_id: item.catalog_home_id || "", documents: item.documents || [] });
        setDialogOpen(true);
    };

    const payloadFromForm = () => {
        const payload = { ...form, documents: form.documents || [] };
        ["beds", "baths", "sqft", "customer_price", "internal_cost", "final_home_only_sales_price", "invoice_without_freight", "freight_financed", "freight_paid", "final_invoice_total", "sales_tax", "discretionary_sales_surtax", "tax_due"].forEach((key) => {
            if (payload[key] === "" || payload[key] === null || payload[key] === undefined) delete payload[key];
        });
        Object.keys(payload).forEach((key) => {
            if (payload[key] === "") payload[key] = null;
        });
        delete payload.documents;
        if (!canManage) {
            ["internal_cost", "invoice_without_freight", "freight_financed", "freight_paid", "final_invoice_total", "sales_tax", "discretionary_sales_surtax", "tax_due", "documents"].forEach((key) => delete payload[key]);
        }
        return payload;
    };

    const save = async () => {
        const payload = payloadFromForm();
        if (!payload.name) {
            toast.error("Model / home name is required");
            return;
        }
        if (!payload.status) {
            toast.error("Inventory status is required");
            return;
        }
        try {
            let savedItemId = editing?.id;
            if (editing) {
                await api.patch(`/home-inventory/${editing.id}`, payload);
                toast.success("Inventory item updated");
            } else {
                const response = await api.post("/home-inventory", payload);
                savedItemId = response.data?.id;
                toast.success("Inventory item added");
            }
            const newLinkedDocuments = (form.documents || []).filter((doc) => !doc.file_id && !doc.document_id && !doc.storage_provider && doc.name && (doc.url || doc.storage_key));
            if (savedItemId && newLinkedDocuments.length) {
                await Promise.all(newLinkedDocuments.map((doc) => api.post(`/home-inventory/${savedItemId}/documents`, doc)));
            }
            setDialogOpen(false);
            await load();
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Save failed"));
        }
    };

    const archiveItem = async (item) => {
        if (!window.confirm(`Archive ${item.name || "this inventory item"}?`)) return;
        try {
            await api.delete(`/home-inventory/${item.id}`);
            toast.success("Inventory item archived");
            await load();
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Archive failed"));
        }
    };

    const restoreItem = async (item) => {
        try {
            await api.post(`/home-inventory/${item.id}/restore`);
            toast.success("Inventory item restored");
            await load();
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Restore failed"));
        }
    };

    const summaryCards = [["On Site", summary.on_site || 0], ["Available", summary.available || 0], ["Ordered", summary.ordered || 0], ["In Transit", summary.in_transit || 0], ["Sold / Reserved", summary.sold_reserved || 0], ["Needs Attention", summary.needs_attention || 0]];

    return (
        <div className="space-y-5" data-testid="home-inventory-page">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"><ArrowLeft className="h-3 w-3" /> Back to dashboard</Link>
                    <h1 className="mt-1 flex items-center gap-2 text-2xl font-semibold text-slate-900"><Warehouse className="h-6 w-6" /> Home Inventory</h1>
                    <p className="mt-1 text-sm text-slate-500">Track actual homes on site, ordered, financed, invoiced, delivered, taxed, and archived.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={load} disabled={loading}><RefreshCcw className="mr-2 h-4 w-4" /> {loading ? "Refreshing" : "Refresh"}</Button>
                    {canManage ? (
                        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                            <DialogTrigger asChild><Button onClick={startCreate} className="bg-[#0F2A47] text-white hover:bg-[#14385E]"><Plus className="mr-2 h-4 w-4" /> Add Inventory Item</Button></DialogTrigger>
                            <DialogContent className="max-w-5xl">
                                <DialogHeader><DialogTitle>{editing ? "Edit Inventory Item" : "Add Inventory Item"}</DialogTitle></DialogHeader>
                                <InventoryForm value={form} setValue={setForm} homes={homes} canSeeCost={canManage} inventoryId={editing?.id} onDocumentsChanged={(docs) => setForm((current) => ({ ...current, documents: docs }))} />
                                <DialogFooter className="gap-2"><Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button onClick={save} className="bg-[#0F2A47] text-white hover:bg-[#14385E]">Save Inventory Item</Button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                    ) : null}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                {summaryCards.map(([label, value]) => <Card key={label} className="border-slate-200 p-4"><div className="text-xs text-slate-500">{label}</div><div className={`mt-1 text-2xl font-semibold ${label === "Needs Attention" && value > 0 ? "text-amber-700" : "text-slate-900"}`}>{value}</div></Card>)}
            </div>

            <Card className="border-slate-200 p-4">
                <div className="grid gap-3 xl:grid-cols-[1fr_repeat(4,160px)_170px_auto]">
                    <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><Input value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") load(); }} placeholder="Search model, manufacturer, serial, HUD, buyer, location" className="pl-9" /></div>
                    <select value={status} onChange={(event) => setStatus(event.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"><option value="__all__">All statuses</option>{STATUS_OPTIONS.map((value) => <option key={value} value={value}>{value}</option>)}</select>
                    <select value={manufacturer} onChange={(event) => setManufacturer(event.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"><option value="__all__">All manufacturers</option>{manufacturers.map((value) => <option key={value} value={value}>{value}</option>)}</select>
                    <select value={homeType} onChange={(event) => setHomeType(event.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"><option value="__all__">All types</option>{types.map((value) => <option key={value} value={value}>{value}</option>)}</select>
                    <select value={financingLender} onChange={(event) => setFinancingLender(event.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"><option value="__all__">All lenders</option>{FINANCING_LENDERS.map((value) => <option key={value} value={value}>{value}</option>)}</select>
                    <select value={quickFilter} onChange={(event) => setQuickFilter(event.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"><option value="all">All active</option><option value="on_site">On-site only</option><option value="available">Available only</option><option value="sold_reserved">Sold/reserved</option><option value="missing_invoice">Missing invoice</option><option value="missing_mco">Missing MCO/MSO</option></select>
                    <Button variant="outline" onClick={load}>Search</Button>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-4">
                    <select value={taxPeriod} onChange={(event) => setTaxPeriod(event.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"><option value="__all__">All tax periods</option>{TAX_PERIODS.map((value) => <option key={value} value={value}>{value}</option>)}</select>
                    <select value={taxStatus} onChange={(event) => setTaxStatus(event.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"><option value="__all__">All sales tax statuses</option>{TAX_FILING_STATUSES.map((value) => <option key={value} value={value}>{value}</option>)}</select>
                    <select value={countyTaxStatus} onChange={(event) => setCountyTaxStatus(event.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"><option value="__all__">All county tax statuses</option>{TAX_FILING_STATUSES.map((value) => <option key={value} value={value}>{value}</option>)}</select>
                    {canManage ? <select value={archiveView} onChange={(event) => setArchiveView(event.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"><option value="active">Active</option><option value="archived">Archived</option><option value="all">Active + archived</option></select> : null}
                </div>
            </Card>

            <div className="grid gap-3 lg:hidden">
                {items.length === 0 ? <Card className="border-slate-200 p-5 text-sm text-slate-500">No inventory items match the current filters.</Card> : null}
                {items.map((item) => {
                    const reasons = attentionReasons(item);
                    const archived = item.archived || item.status === "Archived" || item.active === false;
                    return <Card key={item.id} className="space-y-3 border-slate-200 p-4">
                        <div className="flex items-start justify-between gap-3"><div><div className="font-semibold text-slate-900">{item.name || "Unnamed inventory item"}</div><div className="text-sm text-slate-500">{[item.manufacturer, item.series].filter(Boolean).join(" | ") || "Manufacturer not set"}</div></div><StatusPill status={item.status} /></div>
                        <div className="text-sm text-slate-600">{specLine(item)}</div>
                        {serialLine(item) ? <div className="text-xs text-slate-500">{serialLine(item)}</div> : null}
                        <div className="grid grid-cols-2 gap-2 text-sm"><div><div className="text-xs text-slate-500">Location</div>{locationLine(item)}</div><div><div className="text-xs text-slate-500">Date</div>{importantDate(item)}</div></div>
                        <div className="grid grid-cols-2 gap-2 text-sm"><div><div className="text-xs text-slate-500">Buyer</div>{linkedCustomer(item)}</div><div><div className="text-xs text-slate-500">Financing</div>{[item.financing_lender, item.lender_account_reference].filter(Boolean).join(" | ") || "-"}</div></div>
                        <div className="grid grid-cols-2 gap-2 text-sm"><div><div className="text-xs text-slate-500">Customer price</div>{moneyOrDash(item.customer_price)}</div>{canManage ? <div><div className="text-xs text-slate-500">Internal cost</div>{moneyOrDash(item.internal_cost)}</div> : null}</div>
                        {canManage ? <div className="text-xs text-slate-500">Invoice {item.invoice_number || "-"}</div> : null}
                        <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600"><FileText className="mr-1 inline h-3.5 w-3.5" /> {documentSummary(item)}</div>
                        {reasons.length ? <div className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">Needs attention: {reasons.join("; ")}</div> : null}
                        {canManage ? <div className="flex justify-end gap-2"><Button size="sm" variant="outline" onClick={() => startEdit(item)}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>{archived ? <Button size="sm" variant="outline" onClick={() => restoreItem(item)}><RefreshCcw className="mr-1 h-3.5 w-3.5" /> Restore</Button> : <Button size="sm" variant="outline" onClick={() => archiveItem(item)}><Archive className="mr-1 h-3.5 w-3.5" /> Archive</Button>}</div> : null}
                    </Card>;
                })}
            </div>

            <Card className="hidden border-slate-200 lg:block">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader><TableRow><TableHead>Home</TableHead><TableHead>Status</TableHead><TableHead>Serial / HUD</TableHead><TableHead>Buyer / Financing</TableHead><TableHead>Location</TableHead><TableHead>Dates</TableHead><TableHead>Documents</TableHead><TableHead className="text-right">Price</TableHead>{canManage ? <TableHead className="text-right">Cost</TableHead> : null}<TableHead>Attention</TableHead>{canManage ? <TableHead className="text-right">Actions</TableHead> : null}</TableRow></TableHeader>
                        <TableBody>
                            {items.length === 0 ? <TableRow><TableCell colSpan={canManage ? 11 : 9} className="py-8 text-center text-slate-500">No inventory items match the current filters.</TableCell></TableRow> : items.map((item) => {
                                const reasons = attentionReasons(item);
                                const archived = item.archived || item.status === "Archived" || item.active === false;
                                return <TableRow key={item.id}>
                                    <TableCell><div className="font-medium text-slate-900">{item.name || "Unnamed inventory item"}</div><div className="text-xs text-slate-500">{[item.manufacturer, item.series, specLine(item)].filter(Boolean).join(" | ")}</div></TableCell>
                                    <TableCell><StatusPill status={item.status} /></TableCell>
                                    <TableCell className="text-xs text-slate-500">{serialLine(item) || "-"}</TableCell>
                                    <TableCell className="text-sm text-slate-600">{linkedCustomer(item)}{linkedProject(item) ? <><br />{linkedProject(item)}</> : null}<div className="text-xs text-slate-500">{[item.financing_lender || "No lender", item.lender_account_reference].filter(Boolean).join(" | ")}</div></TableCell>
                                    <TableCell className="text-sm text-slate-600">{locationLine(item)}</TableCell>
                                    <TableCell className="text-xs text-slate-500">Ordered {formatDate(item.ordered_date)}<br />Delivered {formatDate(item.delivered_date)}<br />Sold {formatDate(item.sold_date)}<br />Lot days {item.days_on_lot ?? "-"}</TableCell>
                                    <TableCell className="text-xs text-slate-500">{documentSummary(item)}</TableCell>
                                    <TableCell className="text-right text-sm font-medium tabular-nums">{moneyOrDash(item.customer_price)}</TableCell>
                                    {canManage ? <TableCell className="text-right text-sm text-slate-500 tabular-nums">{moneyOrDash(item.internal_cost)}{item.margin_profit !== null && item.margin_profit !== undefined ? <div className="text-xs">Margin {moneyOrDash(item.margin_profit)}</div> : null}</TableCell> : null}
                                    <TableCell className="max-w-[220px] text-xs text-slate-500">{reasons.length ? <span className="text-amber-800">{reasons.join("; ")}</span> : "-"}</TableCell>
                                    {canManage ? <TableCell className="text-right"><div className="inline-flex gap-1"><Button size="icon" variant="ghost" onClick={() => startEdit(item)} aria-label="Edit inventory item"><Pencil className="h-4 w-4" /></Button>{archived ? <Button size="icon" variant="ghost" onClick={() => restoreItem(item)} aria-label="Restore inventory item"><RefreshCcw className="h-4 w-4" /></Button> : <Button size="icon" variant="ghost" onClick={() => archiveItem(item)} aria-label="Archive inventory item"><Archive className="h-4 w-4" /></Button>}</div></TableCell> : null}
                                </TableRow>;
                            })}
                        </TableBody>
                    </Table>
                </div>
            </Card>
        </div>
    );
}
